import React, { useState, useRef, useEffect } from 'react';
import { X, Send, BrainCircuit, Loader } from 'lucide-react';
import { httpsCallable } from "firebase/functions";

// Recibimos apiKey en las props
const AiChatModal = ({ isOpen, onClose, jobId, jobName, functions, user }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const chatEndRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setMessages([{
                sender: 'ai',
                text: `¡Hola! Soy el asistente de IA para el trabajo ${jobName}. ¿Qué te gustaría saber?`
            }]);
        }
    }, [isOpen, jobName]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || isThinking) return;

        const currentUserMessage = { sender: 'user', text: input };
        setMessages(prev => [...prev, currentUserMessage]);
        setInput('');
        setIsThinking(true);

        try {
            const chatAiJob = httpsCallable(functions, 'chatAiJob');

            // chatAiJob (functions/index.js, desplegada) espera { jobId, userMessage, history }
            // y responde { response }. La API key vive en el servidor (Vertex AI), no se envía.
            //
            // El historial del componente es [{ sender, text }]; la función lo mapea como
            // [{ role, content }] -> Vertex. Convertimos sender->role (ai = "model") y
            // text->content. Vertex exige que el historial empiece en un turno "user",
            // así que descartamos el saludo inicial (y cualquier "model" al frente).
            const history = messages
                .map(m => ({ role: m.sender === 'user' ? 'user' : 'model', content: m.text }));
            while (history.length && history[0].role === 'model') {
                history.shift();
            }

            const result = await chatAiJob({
                jobId: jobId,
                userMessage: input,
                history: history
            });

            const aiResponse = {
                sender: 'ai',
                text: result.data.response || "No pude procesar esa pregunta. Inténtalo de nuevo."
            };
            setMessages(prev => [...prev, aiResponse]);

        } catch (error) {
            console.error("Error al llamar a la función chatAiJob:", error);
            const errorResponse = {
                sender: 'ai',
                text: `Hubo un error al contactar a la IA: ${error.message}. Revisa la consola para más detalles.`
            };
            setMessages(prev => [...prev, errorResponse]);
        } finally {
            setIsThinking(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                <header className="flex justify-between items-center p-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                        <BrainCircuit className="text-indigo-600" size={24} />
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Asistente IA</h2>
                            <p className="text-sm text-gray-500">Consultando sobre: {jobName} (ID: {jobId})</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </header>

                <main className="flex-grow p-4 overflow-y-auto bg-gray-100">
                    <div className="space-y-4">
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex items-end gap-2 ${msg.sender === 'user' ? 'justify-end' : ''}`}>
                                {msg.sender === 'ai' && <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white"><BrainCircuit size={16}/></div>}
                                <div className={`px-4 py-2 rounded-lg max-w-lg ${msg.sender === 'user' ? 'bg-blue-500 text-white rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none'}`}>
                                    <p className="text-sm">{msg.text}</p>
                                </div>
                            </div>
                        ))}
                        {isThinking && (
                            <div className="flex items-end gap-2">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white"><Loader className="animate-spin" size={16}/></div>
                                <div className="px-4 py-2 rounded-lg bg-white text-gray-500">
                                    <p className="text-sm italic">Pensando...</p>
                                </div>
                            </div>
                        )}
                        <div ref={chatEndRef} />
                    </div>
                </main>

                <footer className="p-4 border-t bg-white">
                    <form onSubmit={handleSendMessage} className="flex items-center gap-3">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Escribe tu pregunta aquí..."
                            className="flex-grow p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            disabled={isThinking}
                        />
                        <button type="submit" className="p-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400" disabled={isThinking || !input.trim()}>
                            <Send size={20} />
                        </button>
                    </form>
                </footer>
            </div>
        </div>
    );
};

export default AiChatModal;